// Bridges the two turn-mode HUD buttons -- Wait and Undo -- to the running
// GameScene, the same pub/sub shape roomJump.ts and dpadInput.ts already use, and
// for the same reason: ui.ts owns the DOM and has no scene reference, the scene
// owns the world and has no reason to reach into raw DOM.
//
// Two verbs rather than one module per verb because they arrived together and
// only exist together: both are meaningless on the wall clock. Waiting there is
// what the player does by not pressing anything, and undo there would hand back
// a position the world has already decayed past (see settings.ts).

// The cast pair is a different shape from the two verbs above and needs its own
// explanation.
//
// A cast is already a turn-costing action, but unlike a step it does not go
// through `applyCommand` and cannot easily be made to: the game's cast path is
// `castAction` plus a dozen branches of narration plus the eight effect bridges
// (see ui.ts's performCastUnguarded), where the sim's `cast()` is one call with
// no voice. Routing the game through the latter would throw the narration away,
// which is most of what the game *is*.
//
// So the turn rule reaches it the other way round: ui.ts announces the two edges
// of a cast, and the scene -- which owns the world -- does the snapshotting and
// the clock. Two events rather than one because they do different jobs at
// different moments. `began` fires before any effect runs, because what undo
// wants back is the world as it was while the player still had the choice;
// `ended` fires after every effect has been applied, because a turn's ambient
// rules must run against the world the cast produced, not the one it started
// from.
const castBeganListeners: Array<() => void> = [];
const castEndedListeners: Array<() => void> = [];

const waitListeners: Array<() => void> = [];
const undoListeners: Array<() => void> = [];

export function onCastBegan(listener: () => void): void {
  castBeganListeners.push(listener);
}

export function onCastEnded(listener: () => void): void {
  castEndedListeners.push(listener);
}

export function triggerCastBegan(): void {
  castBeganListeners.forEach((listener) => listener());
}

export function triggerCastEnded(): void {
  castEndedListeners.forEach((listener) => listener());
}

export function onWaitRequested(listener: () => void): void {
  waitListeners.push(listener);
}

export function onUndoRequested(listener: () => void): void {
  undoListeners.push(listener);
}

export function triggerWait(): void {
  waitListeners.forEach((listener) => listener());
}

export function triggerUndo(): void {
  undoListeners.forEach((listener) => listener());
}
